import React, { useState, useEffect } from 'react';
import { Row, Col, Statistic, Divider, Typography, Collapse } from 'antd';
import { DollarOutlined, ToolOutlined, ClockCircleOutlined } from '@ant-design/icons'; // Import icons
import { AnalysisStepsState, SimilarCar } from '../../../types';
import SimilarCarsSection from '../../../components/SimilarCarsSection/SimilarCarsSection';
import {
  FlagsAndHighlights,
  CommonIssues,
  ImageAnalysis,
  MaintenanceRecommendations,
  CpoEligibility,
  RepairAnalysis,
  MarketInsights,
} from './';

interface PageProps {
  results: AnalysisStepsState;
}

interface ValueEligibilityPageProps extends PageProps {
  similarCars: SimilarCar[];
}


export const VehicleOverviewPage: React.FC<PageProps> = ({ results }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const marketValue = results.market_insights?.data?.market_value;
  const repairCost = results.suggested_repairs?.data?.['repair summary']?.['Overall cost estimate'];
  const timeToSell = results.market_insights?.data?.time_to_sell;

  return (
    <div>
      <FlagsAndHighlights
        results={{ flags_and_highlights: results.flags_and_highlights?.data }}
        status={results.flags_and_highlights?.status}
      />
      <div style={{ padding: '24px' }}>
        
        <Row gutter={16} style={{ textAlign: 'center', marginTop: '16px' }}>
          <Col span={isMobile ? 24 : 8}>
            <Statistic
              title={<span>Estimated Market Value <DollarOutlined style={{ fontSize: '1em', verticalAlign: 'middle' }} /></span>}
              value={marketValue || 'N/A'}
              style={{ textAlign: 'center' }}
            />
          </Col>
          {isMobile && <Col span={24}><Divider /></Col>}
          <Col span={isMobile ? 24 : 8}>
            <Statistic
              title={<span>Repair Cost Estimate <ToolOutlined style={{ fontSize: '1em', verticalAlign: 'middle' }} /></span>}
              value={repairCost || 'N/A'}
              style={{ textAlign: 'center' }}
            />
          </Col>
          {isMobile && <Col span={24}><Divider /></Col>}
          <Col span={isMobile ? 24 : 8}>
            <Statistic
              title={<span>Time to Sell <ClockCircleOutlined style={{ fontSize: '1em', verticalAlign: 'middle' }} /></span>}
              value={timeToSell || 'N/A'}
              style={{ textAlign: 'center' }}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};


const { Title } = Typography;

// ... (MechanicalConditionPage and ValueEligibilityPage remain the same)

export const MechanicalConditionPage: React.FC<PageProps> = ({ results }) => {
  const { Panel } = Collapse;
  return (
    <div style={{ padding: '24px' }}>
      <div>
        <Title level={4} style={{ textAlign: 'left', marginBottom: '16px' }}>Issues Summary</Title>
        <RepairAnalysis
          results={{ suggested_repairs: results.suggested_repairs?.data }}
          status={results.suggested_repairs?.status}
        />
      </div>
      <div style={{ marginTop: '32px' }}>
        <Collapse accordion>
          <Panel header={<Title level={4} style={{ margin: 0 }}>Maintenance Recommendations</Title>} key="1">
            <MaintenanceRecommendations
              results={{ maintenance: results.maintenance?.data }}
              status={results.maintenance?.status}
            />
          </Panel>
          <Panel header={<Title level={4} style={{ margin: 0 }}>Image Analysis</Title>} key="2">
            <ImageAnalysis
              results={{ image_analysis: results.image_analysis?.data }}
              status={results.image_analysis?.status}
            />
          </Panel>
          <Panel header={<Title level={4} style={{ margin: 0 }}>Common Issues</Title>} key="3">
            <CommonIssues 
              results={{ common_issues: results.common_issues?.data }}
              status={results.common_issues?.status}
            />
          </Panel>
        </Collapse>
      </div>
    </div>
  );
};



export const ValueEligibilityPage: React.FC<ValueEligibilityPageProps> = ({ results, similarCars }) => {
  const { Panel } = Collapse;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{ padding: '24px' }}>
      <MarketInsights
        results={{ market_insights: results.market_insights?.data }}
        status={results.market_insights?.status}
      />
      <div style={{ marginTop: '32px' }}>
        <Collapse accordion>
          <Panel header={<Title level={4} style={{ margin: 0 }}>Similar Sold Listings</Title>} key="1">
            <SimilarCarsSection similarCars={similarCars} isMobile={isMobile} />
          </Panel>
          <Panel header={<Title level={4} style={{ margin: 0 }}>CPO Eligibility</Title>} key="2">
            <CpoEligibility
              results={{ cpo_eligibility: results.cpo_eligibility?.data }}
              status={results.cpo_eligibility?.status}
            />
          </Panel>
        </Collapse>
      </div>
    </div>
  );
};
